// client/src/routes/portal/notifications.tsx — the destination of §6.6's header bell ·
// PORTAL-002 phase 2.
//
// NOT a fifth tab (§6.6 gives the portal four) and NOT one of §8.16's six portal/link screens —
// §8.16's table has no row for it, which is the gap D-1171 records: §6.6 puts a notification
// bell in the portal header, doc 05 PORTAL-002 lists „own notifications" among the portal's
// surfaces, and neither names a screen for the bell to open. This is that screen, now over
// PORTAL-001's `GET /portal/notifications`.
//
// Its page and its unread filter are §9.2.3 search params, so a tenant who opens the bell,
// filters to the unread and follows a link can come back to exactly that view. `contract` is on
// the layout route's schema and is simply carried: this feed is addressed to the PERSON and not
// to a lease, so nothing here reads it (D-1180).
//
// ── `?settings=1`, the link of every renter notification mail ─────────────────────────────────
// „Preferințe de notificare” in a renter's mail is `/portal/notifications?settings=1` (D-3014,
// `NotificationDeepLinks.PortalNotificationSettings`), which opened the e-mail switches over this
// feed (D-3039). The switches are a section of the settings page now (D-3306), and mails already
// sent keep the old link for as long as somebody keeps the mail — so the link is sent on to that
// section before this screen renders, REPLACING the entry: Back from the switches leaves the
// portal the way the mail's reader came in, rather than bouncing off this route again (D-3309).

import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/layout/PageHeader';
import { NotificationFeed } from '@/features/portal/NotificationFeed';
import {
  portalNotificationsSearchSchema,
  type PortalNotificationsSearch,
} from '@/features/portal/search';

export const Route = createFileRoute('/portal/notifications')({
  validateSearch: portalNotificationsSearchSchema,
  beforeLoad: ({ search }) => {
    if (search.settings === 1) {
      throw redirect({
        to: '/portal/settings',
        // `contract` is the layout's and travels, for D-1180's reason: the tab the reader takes
        // next must show the lease they were reading.
        search: { contract: search.contract, section: 'preferences' },
        replace: true,
      });
    }
  },
  component: PortalNotificationsScreen,
});

function PortalNotificationsScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <>
      <PageHeader
        title={t('portal.notifications.title')}
        description={t('portal.notifications.subtitle')}
      />
      {/* The feed's opening panel is this screen's alone; SCR-OAT-001 embeds the feed without
          it (D-3542). */}
      <NotificationFeed
        hero
        search={search}
        onSearchChange={(next: PortalNotificationsSearch) => {
          // `resetScroll: false` so the PAGER owns the scroll. `main.tsx` creates the
          // router with `scrollRestoration: true`, which jumps the page to the top the
          // instant a search param changes — measured at 216 px to 0 between two 50 ms
          // samples — and that jump beat the pager's smooth scroll every time. The router
          // no longer moves the page on these navigations; `PortalPager` animates it
          // instead, and a filter change simply leaves the reader where they were
          // (D-1674).
          void navigate({ search: (previous) => ({ ...previous, ...next }), resetScroll: false });
        }}
      />
    </>
  );
}
